import * as React from 'react';
import useAuth from '@sdk/authentication/useAuth';
import gettext from '@sdk/lang/gettext';
import { Feature } from '@sdk/common/features';

import getPremiumTooltipId from '../../../premiumGuard/getPremiumTooltipId';
import PremiumGuard from '../../../premiumGuard/PremiumGuard';
import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { PremiumModalNames } from '../../../premiumGuard/constants/modalNames';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

const ShiftExchanges: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const {
    shiftExchangeEnabled,
    shiftDoubleExchangeEnabled,
    usersCanSeeUsersOutsideLocation,
    shiftExchangeOnlyMatchingGroups,
    shiftExchangeRequiresApproval,
  } = userOrgSettings;

  return (
    <>
      <SettingsSection withSeparator>
        <CollapsibleSettingsSection
          isActive={shiftExchangeEnabled || shiftDoubleExchangeEnabled}
          headerItem={
            <>
              <SettingsSwitcherRow
                pBottom={20}
                disabled={!currentUser.isAdmin}
                title={gettext('Allow employees to give away their shifts')}
                subtitle={gettext(
                  'When disabled, employees are not able to offer their shifts or make them available.'
                )}
                value={shiftExchangeEnabled}
                onChange={(value): void => onUserOrgSettingsChange({ shiftExchangeEnabled: value })}
              />

              <PremiumGuard
                premiumGuardModalName={PremiumModalNames.shiftSwap}
                hasAccess={isOrgFeatureEnabled(Feature.doubleExchange)}
                tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.doubleExchange)]}>
                <SettingsSwitcherRow
                  pBottom={20}
                  disabled={!currentUser.isAdmin}
                  title={gettext('Allow employees to swap shifts')}
                  subtitle={gettext(
                    'When disabled, employees are not able to swap their shifts with others.'
                  )}
                  value={shiftDoubleExchangeEnabled}
                  onChange={(value): void =>
                    onUserOrgSettingsChange({ shiftDoubleExchangeEnabled: value })
                  }
                  premiumGuard={getPremiumGuardConfig(Feature.doubleExchange)}
                />
              </PremiumGuard>
            </>
          }
          contentItem={
            <SettingsSwitcherRow
              disabled={!currentUser.isAdmin || !usersCanSeeUsersOutsideLocation}
              title={gettext('Restrict offers and swaps by location and position')}
              subtitle={gettext(
                'When disabled, employees can offer or swap a shift with anyone even if the recipient is not assigned to the location and position of the shift.'
              )}
              value={shiftExchangeOnlyMatchingGroups}
              onChange={(value): void =>
                onUserOrgSettingsChange({ shiftExchangeOnlyMatchingGroups: value })
              }
            />
          }
        />
      </SettingsSection>

      <SettingsSwitcherRow
        pBottom={20}
        disabled={!currentUser.isAdmin}
        title={gettext('Automatically approve applicants')}
        subtitle={gettext(
          'When enabled, shift applications and offers do not require manager’s approval.'
        )}
        value={!shiftExchangeRequiresApproval}
        onChange={(value): void =>
          onUserOrgSettingsChange({ shiftExchangeRequiresApproval: !value })
        }
      />
    </>
  );
};

export default ShiftExchanges;
